// Command Center, Board layout (POC), in the style of a work-management board:
// colored collapsible groups of table rows, full-color status cells with a
// status picker, bulk selection, an item panel, and Group by priority or area.
// Data and actions: useMorningBrief.js. Toggle: CommandCenterSwitch.jsx.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlarmClock, ArrowRight, Bot, Check, CheckCircle2, ChevronDown, Compass, Inbox, NotebookPen, Plug, Search, Undo2, X } from 'lucide-react';
import { entityPath } from '../lib/productInsights';
import { BrainStatus } from '../components/brain/BrainUI';
import WorkspaceInsights from '../components/brain/WorkspaceInsights';
import { AREA_OF_TYPE, TYPE_NAMES, age, born, clock, humanDates, isTyping, safeName, stamp, useMorningBrief, verb } from './useMorningBrief';
import './CommandCenterBoard.css';

const KINDS = {
  issue: { type: 'issues', label: 'Critical issue' },
  readiness: { type: 'initiatives', label: 'Initiative' },
  validated: { type: 'problems', label: 'Problem' },
  stronger: { type: 'problems', label: 'Problem' },
  stale: { type: 'problems', label: 'Stale evidence' },
  decision: { type: 'decisions', label: 'Decision review' },
  experiment: { type: 'experiments', label: 'Experiment' },
};
// Where each kind of prompt belongs, and the label it carries in the Area column.
const AREA_OF = {
  issue: { id: 'fires', label: 'Fire', tone: 'red' },
  decision: { id: 'decisions', label: 'Decision', tone: 'purple' },
  readiness: { id: 'initiatives', label: 'Initiative', tone: 'green' },
  validated: { id: 'discovery', label: 'Problem', tone: 'blue' },
  stronger: { id: 'discovery', label: 'Problem', tone: 'blue' },
  stale: { id: 'discovery', label: 'Problem', tone: 'blue' },
  experiment: { id: 'learning', label: 'Experiment', tone: 'lime' },
};
const STATUS = {
  critical: { label: 'Critical', tone: 'red' },
  overdue: { label: 'Overdue', tone: 'red' },
  due: { label: 'Review due', tone: 'orange' },
  incomplete: { label: 'Incomplete', tone: 'orange' },
  unplanned: { label: 'No initiative', tone: 'purple' },
  outranks: { label: 'Outranks Now', tone: 'purple' },
  stale: { label: 'Stale', tone: 'yellow' },
  snoozed: { label: 'Snoozed', tone: 'grey' },
  dismissed: { label: 'Dismissed', tone: 'grey' },
  signal: { label: 'Unprocessed', tone: 'sky' },
};
function statusOf(s) {
  if (s.status.stamped) return STATUS.dismissed;
  if (s.status.snoozedUntil) return STATUS.snoozed;
  switch (s.kind.prefix) {
    case 'issue': return STATUS.critical;
    case 'decision': return /overdue/.test(s.meta || '') ? STATUS.overdue : STATUS.due;
    case 'readiness': return STATUS.incomplete;
    case 'validated': return STATUS.unplanned;
    case 'stronger': return STATUS.outranks;
    case 'stale': return STATUS.stale;
    default: return STATUS.due;
  }
}
const VIEWS = {
  priority: [
    { id: 'fires', title: 'Fires', tone: 'red', match: s => !s.status.snoozedUntil && s.item.priority === 0 },
    { id: 'today', title: 'Today', tone: 'blue', match: s => !s.status.snoozedUntil && s.item.priority === 1 },
    { id: 'week', title: 'This week', tone: 'purple', match: s => !s.status.snoozedUntil && s.item.priority === 2 },
    { id: 'watch', title: 'Watch', tone: 'orange', match: s => !s.status.snoozedUntil && s.item.priority >= 3 },
    { id: 'snoozed', title: 'Snoozed', tone: 'grey', match: s => Boolean(s.status.snoozedUntil) },
  ],
  area: [
    { id: 'fires', title: 'Fires', tone: 'red', match: s => AREA_OF[s.kind.prefix]?.id === 'fires', to: '/issues', open: 'Open Firefighter' },
    { id: 'decisions', title: 'Decisions', tone: 'purple', match: s => AREA_OF[s.kind.prefix]?.id === 'decisions', to: '/decisions' },
    { id: 'initiatives', title: 'Initiatives', tone: 'green', match: s => AREA_OF[s.kind.prefix]?.id === 'initiatives', to: '/roadmap', open: 'Open Roadmap' },
    { id: 'discovery', title: 'Discovery', tone: 'blue', match: s => AREA_OF[s.kind.prefix]?.id === 'discovery', to: '/discovery' },
    { id: 'learning', title: 'Learning', tone: 'lime', match: s => AREA_OF[s.kind.prefix]?.id === 'learning', to: '/learning' },
  ],
};
const COLLAPSE_KEY = 'spcc:cc-board-collapsed';
const VIEW_KEY = 'spcc:cc-board-view';
const readJSON = (key, fallback) => { try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch { return fallback; } };
const writeJSON = (key, value) => { try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* storage unavailable */ } };
const untilText = iso => new Date(iso).toLocaleString(undefined, { weekday: 'short', hour: '2-digit', minute: '2-digit' });
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// The group's status mix as one bar, so a collapsed group still reads at a glance.
function Battery({ rows }) {
  const counts = {};
  rows.forEach(r => { const st = r.st; counts[st.label] = { ...st, n: (counts[st.label]?.n || 0) + 1 }; });
  const parts = Object.values(counts);
  if (!parts.length) return null;
  const title = parts.map(p => `${p.n} ${p.label}`).join(', ');
  return <span className="bd-battery" title={title} aria-label={`Status mix: ${title}`} role="img">
    {parts.map(p => <i key={p.label} className={`tone-${p.tone}`} style={{ flexGrow: p.n }} />)}
  </span>;
}

function Group({ group, rows = [], hasRows = rows.length > 0, battery = true, cols = 6, collapsed, onToggle, selected, onSelectAll, header, children, empty, count, extra }) {
  const selectable = rows.filter(r => r.selectable);
  const picked = selectable.filter(r => selected.has(r.id)).length;
  const all = selectable.length > 0 && picked === selectable.length;
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.indeterminate = picked > 0 && !all; }, [picked, all]);
  return <section id={`bd-${group.id}`} className={`bd-group tone-${group.tone}${collapsed ? ' is-collapsed' : ''}`} aria-labelledby={`bd-h-${group.id}`}>
    <header className="bd-group-head">
      <button type="button" className="bd-collapse" onClick={onToggle} aria-expanded={!collapsed} aria-controls={`bd-body-${group.id}`} aria-label={`${collapsed ? 'Expand' : 'Collapse'} ${group.title}`}><ChevronDown aria-hidden="true" /></button>
      <h2 id={`bd-h-${group.id}`}>{group.title}</h2>
      <span key={count} className="bd-group-count">{plural(count, 'item', 'items')}</span>
      {extra}
      {battery && <Battery rows={rows} />}
    </header>
    <div id={`bd-body-${group.id}`} className="bd-group-body" inert={collapsed ? '' : undefined}>
      <div className="bd-group-clip">
        <div className="bd-scroll">
          <table className="bd-table">
            <thead><tr>
              <th className="bd-col-check" scope="col">{selectable.length > 0 && <input ref={ref} type="checkbox" checked={all} onChange={() => onSelectAll(selectable.map(r => r.id), !all)} aria-label={`Select all in ${group.title}`} />}</th>
              {header}
            </tr></thead>
            <tbody>{hasRows ? children : <tr className="bd-empty"><td /><td colSpan={cols}>{empty}</td></tr>}</tbody>
          </table>
        </div>
      </div>
    </div>
  </section>;
}

const PROMPT_HEADER = <>
  <th scope="col">Item</th><th className="bd-col-area" scope="col">Area</th><th className="bd-col-status" scope="col">Status</th>
  <th className="bd-col-fact" scope="col">Key fact</th><th className="bd-col-updated" scope="col">Updated</th><th className="bd-col-actions" scope="col"><span className="v2-sr">Actions</span></th>
</>;

export default function CommandCenterBoard({ layoutSwitch = null }) {
  const b = useMorningBrief({ kinds: KINDS, movingClass: 'bd-moving' });
  const { data, loading, workspace, guide, now, sinceText, error, busy, strips, clearedToday, arrivals, record, remove, onKeyDown } = b;
  const navigate = useNavigate();
  const [view, setView] = useState(() => (readJSON(VIEW_KEY, 'priority') === 'area' ? 'area' : 'priority'));
  const [collapsed, setCollapsed] = useState(() => new Set(readJSON(COLLAPSE_KEY, [])));
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [menu, setMenu] = useState(null); // { id, top, left }
  const [panelId, setPanelId] = useState(null);
  const rootRef = useRef(null);
  const lastRow = useRef(null);
  const panelClose = useRef(null);

  // Keyboard-driven changes never animate; pointer-driven ones may.
  useEffect(() => {
    const mark = value => () => { if (rootRef.current) rootRef.current.dataset.input = value; };
    const keys = mark('keys'); const pointer = mark('pointer');
    document.addEventListener('keydown', keys, true); document.addEventListener('pointerdown', pointer, true);
    return () => { document.removeEventListener('keydown', keys, true); document.removeEventListener('pointerdown', pointer, true); };
  }, []);

  const q = query.trim().toLowerCase();
  const matches = text => !q || String(text || '').toLowerCase().includes(q);
  const rows = useMemo(() => strips.map(s => ({ ...s, id: s.item.id, st: statusOf(s), area: AREA_OF[s.kind.prefix] || { label: s.kind.label, tone: 'grey' }, selectable: !s.status.stamped })), [strips]);
  const byId = Object.fromEntries(rows.map(r => [r.id, r]));
  const visible = rows.filter(r => matches(r.item.title) || matches(r.item.detail));
  const inbox = data.inbox.filter(i => i.status !== 'processed' && (matches(i.title) || matches(i.content)))
    .sort((x, y) => new Date(born(y) || 0) - new Date(born(x) || 0))
    .map(i => ({ id: `inbox:${i.id}`, record: i, st: STATUS.signal, selectable: false }));
  const changes = arrivals.filter(n => matches(n.label));
  const groups = VIEWS[view];
  const firstId = groups.map(g => visible.find(g.match)).find(Boolean)?.id;

  const toggleGroup = id => setCollapsed(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); writeJSON(COLLAPSE_KEY, [...next]); return next; });
  const expand = id => setCollapsed(prev => { if (!prev.has(id)) return prev; const next = new Set(prev); next.delete(id); writeJSON(COLLAPSE_KEY, [...next]); return next; });
  const chooseView = v => { setView(v); writeJSON(VIEW_KEY, v); };
  const toggleOne = id => setSelected(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  const selectMany = (ids, on) => setSelected(prev => { const next = new Set(prev); ids.forEach(id => (on ? next.add(id) : next.delete(id))); return next; });
  const live = [...selected].filter(id => byId[id] && !byId[id].status.stamped);

  const snooze = (r, animate = true) => record(r.item, 'snooze', animate);
  const dismiss = (r, animate = true) => record(r.item, 'dismiss', animate);
  const wake = r => remove(r.item, r.status.records.map(f => f.id));
  const undo = r => remove(r.item, [r.status.recordId]);
  const bulk = action => { live.forEach(id => (action === 'snooze' ? snooze(byId[id], false) : dismiss(byId[id], false))); setSelected(new Set()); };

  // Status picker: anchored to the clicked cell, closed by Escape, scroll or an outside click.
  useEffect(() => {
    if (!menu) return undefined;
    const close = e => { if (e.type === 'keydown' && e.key !== 'Escape') return; if (e.type === 'mousedown' && e.target.closest?.('.bd-menu')) return; setMenu(null); };
    document.addEventListener('mousedown', close); document.addEventListener('keydown', close); window.addEventListener('scroll', close, true);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', close); window.removeEventListener('scroll', close, true); };
  }, [menu]);
  const openMenu = (r, e) => { const rect = e.currentTarget.getBoundingClientRect(); setMenu({ id: r.id, top: rect.bottom + 6, left: Math.min(rect.left, window.innerWidth - 230) }); };
  const menuRow = menu && byId[menu.id];

  // Item panel: focus moves in on open and back to the row on close.
  const panelRow = panelId && byId[panelId];
  useEffect(() => { if (panelRow) panelClose.current?.focus(); }, [panelId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!panelId) return undefined;
    const onKey = e => { if (e.key === 'Escape') closePanel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });
  const openPanel = id => { lastRow.current = id; setPanelId(id); };
  function closePanel() { setPanelId(null); const id = lastRow.current; if (id) document.querySelector(`[data-strip="${window.CSS?.escape ? window.CSS.escape(id) : id}"]`)?.focus(); }

  const onBoardKey = e => {
    const row = e.target.closest?.('[data-strip]');
    if (row && row === e.target && !isTyping(e.target) && !e.metaKey && !e.ctrlKey && !e.altKey) {
      const id = row.dataset.strip;
      if (e.key.toLowerCase() === 'x' && byId[id]?.selectable) { e.preventDefault(); toggleOne(id); return; }
      if (e.key === ' ' && byId[id]) { e.preventDefault(); openPanel(id); return; }
      if (e.key === 'Enter' && id.startsWith('inbox:')) { e.preventDefault(); navigate('/inbox'); return; }
    }
    onKeyDown(e);
  };
  const jumpToChanges = () => { expand('changes'); requestAnimationFrame(() => document.getElementById('bd-changes')?.scrollIntoView({ block: 'start', behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' })); };

  const promptRow = (r, i) => {
    const { item, status, meta, isNew, record: rec } = r;
    const checked = selected.has(r.id);
    const updated = age(stamp(rec), now);
    return <tr key={r.id} tabIndex={-1} data-strip={r.id} aria-label={`${r.kind.label}: ${item.title}`} aria-keyshortcuts="J K Enter S E Z X Space" aria-selected={checked}
      className={`bd-row${checked ? ' is-checked' : ''}${status.stamped ? ' is-void' : ''}${panelId === r.id ? ' is-open' : ''}`} style={{ viewTransitionName: safeName(r.id), '--i': Math.min(i, 8) }}
      data-guide={r.id === firstId ? 'brief-first' : undefined}>
      <td className="bd-col-check">{r.selectable && <input type="checkbox" checked={checked} onChange={() => toggleOne(r.id)} aria-label={`Select "${item.title}"`} />}</td>
      <td className="bd-col-item">
        <button type="button" className="bd-item" onClick={() => openPanel(r.id)} title="Open item details">
          <span className="bd-item-title">{item.title}{isNew && <span className="bd-new" title="Not in your previous brief">New to brief</span>}</span>
          <span className={`bd-status-inline tone-${r.st.tone}`}>{r.st.label}</span>
          <span className="bd-item-detail">{status.snoozedUntil ? `Snoozed until ${untilText(status.snoozedUntil)}` : humanDates(item.detail)}</span>
        </button>
      </td>
      <td className="bd-col-area"><span className={`bd-area area-${r.area.id || 'next'}`}>{r.area.label}</span></td>
      <td className="bd-col-status">
        {status.stamped
          ? <span className={`bd-status tone-${r.st.tone}`}>{r.st.label}</span>
          : <button type="button" className={`bd-status tone-${r.st.tone}`} aria-haspopup="menu" aria-expanded={menu?.id === r.id} onClick={e => openMenu(r, e)}>{r.st.label}</button>}
      </td>
      <td className="bd-col-fact">{meta || '—'}</td>
      <td className="bd-col-updated">{updated ? `${updated} ago` : '—'}</td>
      <td className="bd-col-actions">
        {status.stamped
          ? <button type="button" className="bd-text-btn" onClick={() => undo(r)} disabled={!status.recordId}><Undo2 aria-hidden="true" />Undo</button>
          : <>
            <Link className="bd-open" to={item.href} onClick={() => guide?.act(item.id)} aria-label={item.action}>{verb(item.action)}<ArrowRight aria-hidden="true" /></Link>
            {status.snoozedUntil
              ? <button type="button" className="bd-icon-btn" onClick={() => wake(r)} disabled={busy.has(r.id)} aria-label={`Wake "${item.title}"`} title="Wake now"><Undo2 aria-hidden="true" /></button>
              : <button type="button" className="bd-icon-btn" onClick={() => snooze(r)} disabled={busy.has(r.id)} aria-label={`Snooze "${item.title}" for a day`} title="Snooze 1 day (S)"><AlarmClock aria-hidden="true" /></button>}
            <button type="button" className="bd-icon-btn" onClick={() => dismiss(r)} disabled={busy.has(r.id)} aria-label={`Dismiss "${item.title}"`} title="Dismiss (E)"><X aria-hidden="true" /></button>
          </>}
      </td>
    </tr>;
  };

  const panelChanges = panelRow ? arrivals.filter(n => n.type === panelRow.kind.type && String(n.id) === String(panelRow.record?.id)) : [];

  return <div ref={rootRef} className="v2-page bd">
    <header className="bd-head">
      <div>
        <h1>Your morning brief</h1>
        <p>{[workspace?.name, now.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })].filter(Boolean).join(' · ')}
          {!loading && <> · <button type="button" className="bd-link-btn" onClick={jumpToChanges}>{plural(arrivals.length, 'change', 'changes')} {sinceText ? `since ${sinceText.split(' ')[0]}` : 'recently'}</button></>}
          {!loading && clearedToday > 0 && ` · ${clearedToday} cleared today`}</p>
      </div>
      <div className="bd-head-tools">{layoutSwitch}<Link className="bd-primary" to="/inbox" data-guide="capture-signal"><Inbox aria-hidden="true" />Capture a signal</Link></div>
    </header>

    <div className="bd-toolbar">
      <div className="bd-views" role="tablist" aria-label="Group by">
        <button type="button" role="tab" aria-selected={view === 'priority'} onClick={() => chooseView('priority')}>By priority</button>
        <button type="button" role="tab" aria-selected={view === 'area'} onClick={() => chooseView('area')}>By area</button>
      </div>
      <label className="bd-search"><Search aria-hidden="true" /><span className="v2-sr">Search the brief</span>
        <input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search items" />
      </label>
      <p className="bd-keys" aria-hidden="true"><kbd>J</kbd><kbd>K</kbd> move · <kbd>Space</kbd> details · <kbd>Enter</kbd> open · <kbd>X</kbd> select · <kbd>S</kbd> snooze · <kbd>E</kbd> dismiss · <kbd>Z</kbd> undo</p>
    </div>
    <BrainStatus quietRefresh />
    {error && <div className="v2-error bd-error" role="alert">{error}</div>}

    <div className="bd-board" onKeyDown={onBoardKey}>
      {loading ? <div className="bd-loading" aria-hidden="true"><span /><span /><span /></div> : <>
        {!rows.length && !q && <div className="bd-allclear"><Check aria-hidden="true" /><div><h2>{data.problems.length ? 'Nothing in your records needs a decision right now.' : 'Start with a customer problem.'}</h2><p>Capture a signal in Inbox, or add a problem in Discovery. Your board fills from the evidence and initiatives you save.</p></div></div>}
        {groups.map(g => {
          const groupRows = visible.filter(g.match);
          return <Group key={`${view}-${g.id}`} group={g} rows={groupRows} count={groupRows.filter(r => !r.status.stamped).length} collapsed={collapsed.has(g.id)} onToggle={() => toggleGroup(g.id)}
            selected={selected} onSelectAll={selectMany} header={PROMPT_HEADER}
            empty={q ? 'No matching items.' : g.id === 'fires' ? 'No critical issues open.' : 'Nothing here.'}
            extra={g.to && <Link className="bd-group-link" to={g.to}>{g.open || `Open ${g.title}`}<ArrowRight aria-hidden="true" /></Link>}>
            {groupRows.map(promptRow)}
          </Group>;
        })}
        <Group group={{ id: 'inbox', title: 'Inbox signals', tone: 'sky' }} rows={inbox} cols={4} count={inbox.length} collapsed={collapsed.has('inbox')} onToggle={() => toggleGroup('inbox')}
          selected={selected} onSelectAll={selectMany} empty={q ? 'No matching signals.' : 'No unprocessed signals.'}
          extra={<Link className="bd-group-link" to="/inbox">Open Inbox<ArrowRight aria-hidden="true" /></Link>}
          header={<><th scope="col">Signal</th><th className="bd-col-status" scope="col">Status</th><th className="bd-col-updated" scope="col">Captured</th><th className="bd-col-actions" scope="col"><span className="v2-sr">Actions</span></th></>}>
          {inbox.map((r, i) => <tr key={r.id} tabIndex={-1} data-strip={r.id} aria-label={`Signal: ${r.record.title || 'Untitled signal'}`} aria-keyshortcuts="J K Enter" className="bd-row" style={{ '--i': Math.min(i, 8) }}>
            <td className="bd-col-check" />
            <td className="bd-col-item"><span className="bd-item is-static"><span className="bd-item-title">{r.record.title || r.record.content?.slice(0, 90) || 'Untitled signal'}</span><span className="bd-item-detail">Review and file it in Inbox</span></span></td>
            <td className="bd-col-status"><span className={`bd-status tone-${r.st.tone}`}>{r.st.label}</span></td>
            <td className="bd-col-updated">{born(r.record) ? clock(born(r.record), now) : '—'}</td>
            <td className="bd-col-actions"><Link className="bd-open" to="/inbox">Review<ArrowRight aria-hidden="true" /></Link></td>
          </tr>)}
        </Group>
        <Group group={{ id: 'changes', title: sinceText ? `Changed since ${sinceText}` : 'Recently updated', tone: 'grey' }} hasRows={changes.length > 0} battery={false} cols={4} count={changes.length}
          collapsed={collapsed.has('changes')} onToggle={() => toggleGroup('changes')} selected={selected} onSelectAll={selectMany}
          empty={q ? 'No matching changes.' : 'No records changed since then.'}
          header={<><th scope="col">Record</th><th className="bd-col-area" scope="col">Type</th><th scope="col">What changed</th><th className="bd-col-updated" scope="col">When</th></>}>
          {changes.map((n, i) => <tr key={`${n.type}:${n.id}`} className="bd-row" style={{ '--i': Math.min(i, 8) }}>
            <td className="bd-col-check" />
            <td className="bd-col-item"><Link className="bd-item is-link" to={entityPath(n.type, n.id)}><span className="bd-item-title">{n.label}</span></Link></td>
            <td className="bd-col-area"><span className={`bd-area area-${AREA_OF_TYPE[n.type] || 'next'}`}>{(TYPE_NAMES[n.type] || n.type).replace(/^./, c => c.toUpperCase())}</span></td>
            <td className="bd-col-change">{n.change}</td>
            <td className="bd-col-updated"><time dateTime={n.at}>{clock(n.at, now)}</time></td>
          </tr>)}
        </Group>
      </>}
    </div>

    <aside className="bd-side" aria-label="Shortcuts and insights">
      <nav className="bd-shortcuts" aria-label="Shortcuts">
        {guide?.activation?.path === 'jira' && !data.imports.length && <Link to="/onboarding"><Plug aria-hidden="true" />Setup &amp; integrations</Link>}
        <Link to="/autopilot"><Bot aria-hidden="true" />Agent team</Link>
        <Link to="/brain"><Compass aria-hidden="true" />Product Brain</Link>
        <Link to="/decisions"><NotebookPen aria-hidden="true" />Record a decision</Link>
        <Link to="/learning"><CheckCircle2 aria-hidden="true" />Review learning</Link>
      </nav>
      <WorkspaceInsights area="command_center" />
    </aside>

    {live.length > 0 && <div className="bd-bulk" role="region" aria-label="Bulk actions">
      <span className="bd-bulk-count">{live.length}</span><span>{live.length === 1 ? 'item selected' : 'items selected'}</span>
      <button type="button" className="bd-bulk-btn" onClick={() => bulk('snooze')}><AlarmClock aria-hidden="true" />Snooze</button>
      <button type="button" className="bd-bulk-btn" onClick={() => bulk('dismiss')}><X aria-hidden="true" />Dismiss</button>
      <button type="button" className="bd-icon-btn" onClick={() => setSelected(new Set())} aria-label="Clear selection"><X aria-hidden="true" /></button>
    </div>}

    {menuRow && <div className="bd-menu" role="menu" aria-label={`Status of ${menuRow.item.title}`} style={{ top: menu.top, left: menu.left }}>
      <Link role="menuitem" className="bd-menu-item tone-blue" to={menuRow.item.href} onClick={() => { guide?.act(menuRow.id); setMenu(null); }}>{menuRow.item.action}</Link>
      {menuRow.status.snoozedUntil
        ? <button type="button" role="menuitem" className="bd-menu-item tone-green" onClick={() => { wake(menuRow); setMenu(null); }}>Wake now</button>
        : <button type="button" role="menuitem" className="bd-menu-item tone-grey" onClick={() => { snooze(menuRow); setMenu(null); }}>Snooze 1 day</button>}
      <button type="button" role="menuitem" className="bd-menu-item tone-red" onClick={() => { dismiss(menuRow); setMenu(null); }}>Dismiss</button>
    </div>}

    {panelRow && <>
      <div className="bd-scrim" onClick={closePanel} aria-hidden="true" />
      <aside className="bd-panel" role="dialog" aria-modal="false" aria-labelledby="bd-panel-title">
        <header className="bd-panel-head">
          <span className={`bd-area area-${panelRow.area.id || 'next'}`}>{panelRow.area.label}</span>
          <span className={`bd-status is-static tone-${panelRow.st.tone}`}>{panelRow.st.label}</span>
          <button ref={panelClose} type="button" className="bd-icon-btn" onClick={closePanel} aria-label="Close details"><X aria-hidden="true" /></button>
        </header>
        <h2 id="bd-panel-title">{panelRow.item.title}</h2>
        <p className="bd-panel-reason">{humanDates(panelRow.item.detail)}</p>
        <dl className="bd-panel-facts">
          <div><dt>Type</dt><dd>{panelRow.kind.label}</dd></div>
          {panelRow.meta && <div><dt>Key fact</dt><dd>{panelRow.meta}</dd></div>}
          {panelRow.status.snoozedUntil && <div><dt>Snoozed until</dt><dd>{untilText(panelRow.status.snoozedUntil)}</dd></div>}
          {age(stamp(panelRow.record), now) && <div><dt>Record updated</dt><dd>{age(stamp(panelRow.record), now)} ago</dd></div>}
          <div><dt>Source</dt><dd>{panelRow.item.source || 'Calculated from your records'}</dd></div>
          {panelRow.isNew && <div><dt>In brief</dt><dd>New since your previous brief</dd></div>}
        </dl>
        {!panelRow.status.stamped && <div className="bd-panel-actions">
          <Link className="bd-primary" to={panelRow.item.href} onClick={() => guide?.act(panelRow.id)}>{panelRow.item.action}<ArrowRight aria-hidden="true" /></Link>
          {panelRow.status.snoozedUntil
            ? <button type="button" className="bd-secondary" onClick={() => wake(panelRow)}><Undo2 aria-hidden="true" />Wake now</button>
            : <button type="button" className="bd-secondary" onClick={() => { snooze(panelRow); closePanel(); }}><AlarmClock aria-hidden="true" />Snooze 1 day</button>}
          <button type="button" className="bd-secondary" onClick={() => { dismiss(panelRow); closePanel(); }}><X aria-hidden="true" />Dismiss</button>
        </div>}
        <section className="bd-panel-activity" aria-labelledby="bd-panel-activity">
          <h3 id="bd-panel-activity">Activity since your last visit</h3>
          {panelChanges.length ? <ul>{panelChanges.map(n => <li key={`${n.type}:${n.id}:${n.at}`}><strong>{n.change}</strong><time dateTime={n.at}>{clock(n.at, now)}</time></li>)}</ul> : <p>No changes to this record since then.</p>}
          {panelRow.record && <Link className="bd-link" to={entityPath(panelRow.kind.type, panelRow.record.id)}>View in Product Brain<ArrowRight aria-hidden="true" /></Link>}
        </section>
      </aside>
    </>}
  </div>;
}
